import { faShare } from "@fortawesome/pro-light-svg-icons"
import {
  AuthCode,
  Button,
  capitalizeFirstLetter,
  Checkbox,
  Gender,
  JoonUIColor,
  Modal,
  ModalHeader,
  SegmentedControl,
  TextInput,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import { useMutation } from "@tanstack/react-query"
import dayjs from "dayjs"
import { useState } from "react"
import toast from "react-hot-toast"

import { useAddFamilyMemberModalStore } from "./hooks/useAddFamilyMemberModalStore"
import useFamilyUserSelectStore from "./hooks/useFamilyUserSelect"
import DateInput from "../../components/dateInput/DateInput"
import QuestionnaireDisclaimer from "../../components/onboarding/questionnaires/QuestionnaireDisclaimer"
import QuestionnaireSection from "../../components/onboarding/questionnaires/QuestionnaireSection"
import { GENDER_OPTIONS, QUERY_KEYS } from "../../constants"
import { submitQuestionnaire } from "../../networking/authentication"
import { addChild, loadFamilies } from "../../networking/families/api"
import {
  useFamilyQuery,
  useParentAuthCodeQuery,
  useUserQuery,
} from "../../networking/queries"
import { useChildQuestionnairesQuery } from "../../networking/questionnaires/queries"
import {
  AddChildStep,
  QuestionAnswerMappingInterface,
  QuestionnaireType,
} from "../../types"
import { queryClient } from "../../util/queryClient"
import { isValidDate, separateFamily } from "../../util/util"

const AddFamilyMemberModal = () => {
  const { isOpen, onClose, type, setType, step } =
    useAddFamilyMemberModalStore()

  const options = [
    { label: "Another child", value: UserRole.CHILD },
    { label: "Another parent", value: UserRole.PARENT },
  ]

  if (step === AddChildStep.QUESTIONS || step === AddChildStep.DISCLAIMER) {
    return <QuestionnaireModal />
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      style={{ width: "min(450px, 100vw)" }}
    >
      <ModalHeader
        title="Add Family Member"
        onClose={onClose}
        showBorderBottom
      />
      <div className="flex flex-col p-6 pt-4 overflow-y-auto">
        <div className="flex flex-col gap-2">
          <Typography variant="body">Who would you like to add?</Typography>
          <SegmentedControl
            options={options}
            value={type}
            fullWidth
            setValue={(value) => setType(value as UserRole)}
          />
        </div>
        {type === UserRole.CHILD ? <AddChildView /> : <AddParentView />}
      </div>
    </Modal>
  )
}

export default AddFamilyMemberModal

const AddChildView = () => {
  const {
    name,
    setName,
    birthday,
    setBirthday,
    step,
    setStep,
    gender,
    setGender,
  } = useAddFamilyMemberModalStore()
  const [error, setError] = useState("")

  const handleNext = () => {
    if (birthday && !isValidDate(birthday)) {
      setError("Invalid birthday")
      return
    }
    if (!name) {
      setError("Name is required")
      return
    }
    setError("")
    setStep(AddChildStep.DISCLAIMER)
  }

  if (step === AddChildStep.DISCLAIMER) {
    return <div>Disclaimer</div>
  } else if (step === AddChildStep.QUESTIONS) {
    return <div>Questions</div>
  } else
    return (
      <div className="flex flex-col gap-6 mt-6">
        <div className="flex flex-col gap-1 w-full">
          <Typography variant="body">Name</Typography>
          <TextInput
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ron"
            fullWidth
          />
        </div>
        <div className="flex flex-col gap-1 w-full">
          <Typography variant="body">Gender</Typography>
          <div className="flex flex-col border border-borderPrimary rounded-md">
            {GENDER_OPTIONS.map((genderOption) => (
              <div className="border-b border-borderPrimary last:border-none p-3">
                <button
                  onClick={() => setGender(genderOption.value)}
                  className="w-full flex flex-row items-center gap-2 justify-between"
                >
                  <Typography variant="body">
                    {capitalizeFirstLetter(genderOption.label)}
                  </Typography>
                  <Checkbox
                    selected={gender === genderOption.value}
                    name="gender"
                    hideBorder
                    inputType="radio"
                    onChange={() => setGender(genderOption.value)}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1 w-full">
          <Typography variant="body">
            Birthday{" "}
            <Typography variant="bodyXSmall" color={JoonUIColor.text.secondary}>
              (optional)
            </Typography>
          </Typography>
          <DateInput
            value={birthday ?? undefined}
            onChange={(e) => setBirthday(e)}
            name="birthdate"
            placeholder="MM/DD/YYYY"
            fullWidth
          />
        </div>
        {error && (
          <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
            {error}
          </Typography>
        )}
        <Button onClick={handleNext} text="Next" fullWidth />
      </div>
    )
}

const AddParentView = () => {
  const { data: authCode } = useParentAuthCodeQuery()

  const onClickShareCode = () => {
    if (authCode) shareCoparentCode(authCode)
  }
  return (
    <div className="flex flex-col gap-6 mt-6">
      <Typography variant="body">
        Share this one-time code with the other parent and they can use it when
        creating an account!
      </Typography>
      <div className="flex justify-center border border-borderPrimary rounded-md py-2 px-4">
        <AuthCode code={authCode || ""} />
      </div>
      <Button
        onClick={onClickShareCode}
        text="Share"
        buttonType="secondary"
        fullWidth
        icon={faShare}
      />
    </div>
  )
}

const QuestionnaireModal = () => {
  const { step, setStep, name, onClose, birthday, isOpen, gender } =
    useAddFamilyMemberModalStore()
  const { setSelectedUserId } = useFamilyUserSelectStore()
  const showQuestionnaire =
    (isOpen && step === AddChildStep.DISCLAIMER) ||
    step === AddChildStep.QUESTIONS

  const { data: family } = useFamilyQuery()
  const { user } = useUserQuery()

  const { data: childQuestionnaires } = useChildQuestionnairesQuery()

  const addChildMutation = useMutation(addChild)

  const submitAddChild = async ({
    questionAnswerMapping,
  }: {
    questionAnswerMapping: QuestionAnswerMappingInterface
  }) => {
    if (!family) return
    const newInfo = {
      name: name,
      birthdate: birthday ? dayjs(birthday).format("YYYY-MM-DD") : undefined,
      gender: gender || Gender.OTHER,
    }

    await addChildMutation.mutateAsync({
      childInfo: newInfo,
      familyId: family?.id,
    })

    await queryClient.invalidateQueries([QUERY_KEYS.FAMILY])
    toast.success("Child added!")
    onClose()

    // Submit questionnaires
    const families = await loadFamilies()
    const { children } = separateFamily(user?.id, families)
    const newChild = children.filter((child) => child.user.name === name)[0]

    setTimeout(() => setSelectedUserId(newChild.user.id), 1000)

    // For each type of child questionnaire,
    // submit the answers for that questionnaire, as found in questionAnswerMapping
    childQuestionnaires?.questionIdMap?.forEach((questionnaire: any) => {
      const answers = questionnaire.questions.map((question_id: any) => ({
        question_id,
        answer_ids: questionAnswerMapping[question_id],
      }))
      submitQuestionnaire(questionnaire.id, newChild.user.id, answers)
    })
  }
  return (
    <Modal
      isOpen={showQuestionnaire}
      onClose={() => setStep(AddChildStep.NAME)}
      mobileFull
    >
      <div className="flex overflow-y-auto h-full ">
        {step === AddChildStep.DISCLAIMER ? (
          <QuestionnaireDisclaimer
            childName={name}
            onConfirm={() => setStep(AddChildStep.QUESTIONS)}
            questionnaireType={QuestionnaireType.EDIT_FAMILY}
          />
        ) : (
          <QuestionnaireSection
            questions={childQuestionnaires?.allQuestions}
            previousStep={() => setStep(AddChildStep.DISCLAIMER)}
            onConfirm={submitAddChild}
            questionnaireType={QuestionnaireType.EDIT_FAMILY}
          />
        )}
      </div>
    </Modal>
  )
}

const shareCoparentCode = async (code: string) => {
  const subjectNew = "Join our family on Joon!"
  const instructionsNew = `Use the code ${code} to join our family on Joon! 
  
https://my.joonapp.io/welcome`

  try {
    // If the web share API is supported by the browser
    if (navigator.share) {
      await navigator.share({
        title: subjectNew,
        text: instructionsNew,
        url: "https://joonapp.io/download-redirect",
      })
    } else {
      // Else open email to share
      const emailSubject = encodeURIComponent(subjectNew)
      const emailBody = encodeURIComponent(instructionsNew)

      const url = `mailto:?subject=${emailSubject}&body=${emailBody}`
      window.open(url)
    }
  } catch (error) {
    console.error("Error sharing:", error)
  }
}
